/**
 * Phase 8 · Cluster C-44 — GitLab sourcing connector.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.7
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.8
 *
 * Mirror of `github.ts` for GitLab.com:
 *   - GET https://gitlab.com/api/v4/users?search=<q>&per_page=N
 *   - GET https://gitlab.com/api/v4/users/:id  (per-hit hydrate for bio)
 *
 * Auth:
 *   - auth_kind='api_key' → GitLab personal access token with `read_user`.
 *     Like GitHub, authed rate limits only apply through the Edge
 *     Function proxy (future work); direct-from-browser falls back to
 *     unauthenticated, which GitLab.com permits with a looser rate
 *     ceiling than individual project searches.
 *
 * Read-only. No projects hydration at search time — see the DON'T list.
 */
import { supabase } from '@core/supabase/client';
import { sourceConnectorsService } from '@core/supabase/services/sourceConnectorsService';
import {
  clampLimit,
  ConnectorNotConfiguredError,
  ConnectorTransportError,
  type ConnectorHealth,
  type SourcingConnector,
  type SourcingQuery,
  type SourcingResult,
} from './types';

const UNAUTH_PER_MIN = 60;
const UNAUTH_WINDOW_MS = 60 * 1000;
const AUTHED_PER_MIN = 2000;
const AUTHED_WINDOW_MS = 60 * 1000;
const PROXY_FN = 'ggj_b2b_sourcing_proxy';
const GL_USERS_URL = 'https://gitlab.com/api/v4/users';

interface BucketState {
  timestamps: number[];
}
const rateBuckets = new Map<string, BucketState>();

function acquireToken(orgId: string, authed: boolean): boolean {
  const cap = authed ? AUTHED_PER_MIN : UNAUTH_PER_MIN;
  const windowMs = authed ? AUTHED_WINDOW_MS : UNAUTH_WINDOW_MS;
  const key = `gitlab:${orgId}:${authed ? 'a' : 'u'}`;
  const now = Date.now();
  const state = rateBuckets.get(key) ?? { timestamps: [] };
  state.timestamps = state.timestamps.filter((t) => now - t < windowMs);
  if (state.timestamps.length >= cap) {
    rateBuckets.set(key, state);
    return false;
  }
  state.timestamps.push(now);
  rateBuckets.set(key, state);
  return true;
}

interface GlUserHit {
  id: number;
  username: string;
  name: string;
  state?: string;
  avatar_url?: string | null;
  web_url?: string | null;
}

interface GlUserDetail extends GlUserHit {
  bio?: string | null;
  location?: string | null;
  linkedin?: string | null;
  public_email?: string | null;
  organization?: string | null;
  job_title?: string | null;
  projects_limit?: number | null;
  last_activity_on?: string | null;
  groups?: string[] | null;
}

/**
 * GitLab's `/users` search only accepts a single `search` param that
 * matches against username / name / public email. We join the free
 * keywords with a space; skill qualifiers have no equivalent here and
 * are silently dropped from the server query (they still filter after
 * we hydrate profile + bio).
 */
function buildGitlabSearch(q: SourcingQuery): string {
  const keywords = (q.keywords ?? []).map((s) => s.trim()).filter(Boolean);
  return keywords.join(' ');
}

function hitToResult(hit: GlUserHit, detail: GlUserDetail | null): SourcingResult {
  const displayName = detail?.name?.trim() || hit.name || hit.username;
  const headline = [detail?.job_title, detail?.organization]
    .filter((s): s is string => Boolean(s && s.trim()))
    .join(' @ ') || (detail?.bio?.trim() || undefined);
  return {
    externalId: String(hit.id ?? hit.username),
    name: displayName,
    headline,
    locationText: detail?.location ?? undefined,
    profileUrl: detail?.web_url ?? hit.web_url ?? `https://gitlab.com/${hit.username}`,
    avatarUrl: detail?.avatar_url ?? hit.avatar_url ?? undefined,
    signals: {
      username: hit.username,
      projects: detail?.projects_limit ?? null,
      groups: detail?.groups ?? [],
      lastActivityOn: detail?.last_activity_on ?? null,
    },
    source: 'gitlab',
  };
}

async function tryProxy(
  orgId: string,
  search: string,
  limit: number,
  skills: string[] | undefined,
  location: string | undefined,
): Promise<{ users: GlUserDetail[] } | null> {
  try {
    const { data, error } = await supabase.functions.invoke(PROXY_FN, {
      body: {
        action: 'gitlab.search',
        orgId,
        query: { search, limit, skills: skills ?? [], location: location ?? null },
      },
    });
    if (error) return null;
    const payload = (data ?? null) as { users?: GlUserDetail[] } | null;
    if (!payload || !Array.isArray(payload.users)) return null;
    return { users: payload.users };
  } catch {
    return null;
  }
}

async function directSearch(search: string, limit: number): Promise<GlUserHit[]> {
  // GitLab.com accepts an empty `search` → returns newest users globally,
  // which is rarely what we want. Bail early so the tool_result summary
  // shows "0" instead of random people.
  if (!search.trim()) return [];
  const url = `${GL_USERS_URL}?search=${encodeURIComponent(search)}&per_page=${limit}&active=true`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) {
    throw new ConnectorTransportError('gitlab', `search ${res.status}`);
  }
  const json = (await res.json()) as GlUserHit[] | unknown;
  return Array.isArray(json) ? (json as GlUserHit[]).slice(0, limit) : [];
}

async function directDetail(id: number): Promise<GlUserDetail | null> {
  try {
    const res = await fetch(`${GL_USERS_URL}/${id}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    return (await res.json()) as GlUserDetail;
  } catch {
    return null;
  }
}

export const gitlabConnector: SourcingConnector = {
  kind: 'gitlab',

  async search(query: SourcingQuery, orgId: string): Promise<SourcingResult[]> {
    if (!orgId) return [];

    const row = await sourceConnectorsService.get(orgId, 'gitlab');
    if (row && row.status !== 'active') {
      throw new ConnectorNotConfiguredError('gitlab', `status=${row.status}`);
    }
    const hasToken = Boolean(row && row.authKind === 'api_key');

    if (!acquireToken(orgId, hasToken)) {
      throw new ConnectorTransportError(
        'gitlab',
        hasToken
          ? `rate limit exceeded (${AUTHED_PER_MIN}/minute)`
          : `rate limit exceeded (${UNAUTH_PER_MIN}/minute) — configure a token for higher limits`,
      );
    }

    const limit = clampLimit(query.limit);
    const search = buildGitlabSearch(query);

    const proxyBundle = await tryProxy(
      orgId,
      search,
      limit,
      query.skills,
      query.location,
    );

    let hits: GlUserHit[] = [];
    const details: Record<number, GlUserDetail | null> = {};
    if (proxyBundle) {
      hits = proxyBundle.users;
      for (const u of proxyBundle.users) details[u.id] = u;
    } else {
      hits = await directSearch(search, limit);
      await Promise.all(
        hits.map(async (h) => {
          details[h.id] = await directDetail(h.id);
        }),
      );
    }

    // Post-filter by skills / location when the server query couldn't
    // express them. `bio` is the most reliable place skills appear.
    const wantedSkills = (query.skills ?? [])
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const wantedLocation = query.location?.trim().toLowerCase();
    const filtered = hits.filter((h) => {
      const d = details[h.id];
      if (wantedSkills.length) {
        const hay = [d?.bio ?? '', d?.job_title ?? '', h.name ?? '', h.username ?? '']
          .join(' ')
          .toLowerCase();
        if (!wantedSkills.some((w) => hay.includes(w))) return false;
      }
      if (wantedLocation) {
        const loc = (d?.location ?? '').toLowerCase();
        if (!loc.includes(wantedLocation)) return false;
      }
      return true;
    });

    if (row) void sourceConnectorsService.markUsed(orgId, 'gitlab');

    return filtered.slice(0, limit).map((h) => hitToResult(h, details[h.id] ?? null));
  },

  async healthcheck(orgId: string): Promise<ConnectorHealth> {
    const row = await sourceConnectorsService.get(orgId, 'gitlab');
    if (!row) return { ok: true, reason: 'unauthenticated' };
    if (row.status !== 'active') return { ok: false, reason: row.status };
    return { ok: true };
  },
};
